import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useValidateResetTokenQuery, useResetPasswordMutation } from "@/api/authApi";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import logo from "@/assets/logo.svg";

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data: validation, isLoading: isValidating, isError } = useValidateResetTokenQuery(token || "", {
    skip: !token
  });
  
  const [resetPassword, { isLoading: isResetting, isSuccess }] = useResetPasswordMutation();
  
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      await resetPassword({ token: token!, password: formData.password }).unwrap();
      toast.success("Password reset successfully");
    } catch (err: any) {
      const message = err.data?.message || "Failed to reset password";
      setError(message);
      toast.error(message);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F8FA]">
        <Loader2 className="w-12 h-12 animate-spin text-[#01889F]" />
      </div>
    );
  }

  if (isError || (validation && !validation.valid)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F7F9] p-4 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#012C4C] mb-2">Invalid or Expired Link</h2>
          <p className="text-[#64748B] mb-6">
            The password reset link you clicked is invalid or has expired after 10 minutes. 
            Please request a new password reset link.
          </p>
          <button 
            onClick={() => navigate("/login")}
            className="w-full bg-[#012C4C] text-white py-3 rounded-xl font-semibold hover:bg-[#011C3C] transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F7F9] p-4 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-emerald-100">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#012C4C] mb-2">Password Updated!</h2>
          <p className="text-[#64748B] mb-6">
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <button 
            onClick={() => navigate("/login")}
            className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
          >
            Log In Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F7F9] p-4">
      <div className="w-full max-w-[440px] flex flex-col items-center text-center bg-white p-10 rounded-3xl shadow-2xl border border-slate-100">
        
        {/* Logo Section */}
        <div className="mb-8">
          <img src={logo} alt="MedXPro Health" className="h-[60px] w-auto object-contain" />
        </div>

        <div className="space-y-3 mb-10">
          <h1 className="text-[28px] font-bold text-[#012C4C] leading-tight">
            Create New Password
          </h1>
          <p className="text-[16px] text-[#64748B]">
            Ensure your account stays secure
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="space-y-2 text-left">
            <label className="text-[14px] font-bold text-[#012C4C] ml-1">
              New Password
            </label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                required
                className="w-full h-14 px-4 pr-12 rounded-xl bg-[#F5F8FA] border border-[#EEEEEE] text-[#012C4C] focus:outline-none focus:border-[#01889F] transition-all"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7D7D7D] hover:text-[#012C4C]"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="space-y-2 text-left">
            <label className="text-[14px] font-bold text-[#012C4C] ml-1">
              Confirm Password
            </label>
            <input
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              required
              className="w-full h-14 px-4 rounded-xl bg-[#F5F8FA] border border-[#EEEEEE] text-[#012C4C] focus:outline-none focus:border-[#01889F] transition-all"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-500 text-sm py-3 px-4 rounded-lg flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isResetting}
            className="w-full h-14 bg-[#012C4C] text-white rounded-xl font-bold text-[16px] hover:bg-[#011C3C] transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70"
          >
            {isResetting ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              "Update Password"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
